import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import { UOM_GROUP_ORDER, matchUomChoice, uomChoices } from '@/lib/uom';

const NONE = '__none__';

interface UomSelectProps {
  value: string;
  onChange: (uom: string) => void;
  id?: string;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  /** Only offer units that convert to this one. */
  convertibleTo?: string | null;
  /** Adds a first option that clears the value (onChange receives ''). */
  emptyLabel?: string;
  placeholder?: string;
}

/** Pick a unit of measure from the standard list. No free typing. */
export function UomSelect({
  value,
  onChange,
  id,
  ariaLabel,
  className,
  disabled,
  convertibleTo,
  emptyLabel,
  placeholder = 'Pick a unit',
}: UomSelectProps) {
  const choices = useMemo(() => uomChoices(value, convertibleTo), [value, convertibleTo]);
  const selected = matchUomChoice(value, choices) || (emptyLabel ? NONE : '');
  const groups = UOM_GROUP_ORDER.map((g) => ({
    group: g,
    options: choices.filter((o) => o.group === g),
  })).filter((g) => g.options.length > 0);

  return (
    <Select
      value={selected || undefined}
      onValueChange={(v) => onChange(v === NONE ? '' : v)}
      disabled={disabled}
    >
      <SelectTrigger id={id} aria-label={ariaLabel} className={cn(className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {emptyLabel && (
          <>
            <SelectItem value={NONE}>{emptyLabel}</SelectItem>
            <SelectSeparator />
          </>
        )}
        {groups.map((g, i) => (
          <SelectGroup key={g.group}>
            {i > 0 && <SelectSeparator />}
            {groups.length > 1 && g.group !== 'Other' && <SelectLabel>{g.group}</SelectLabel>}
            {g.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}
